// Anomalien im Feld — deterministisch, aus einer **eigenen** Ziehungsfolge.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/anomalies.py`.**
//
// Der entscheidende Punkt — dasselbe Muster wie bei den Gegnern: die Folge hat
// mit der Brockenfolge nichts zu tun. Eine Ziehung dort einzufuegen verschoebe
// jede folgende, und Client und Server saehen verschiedene Welten, ohne dass
// irgendwo ein Fehler auftaucht (Gates FG-20, FG-30).
//
// IDs liegen in einem eigenen Raum (`a:cx:cy:0`), damit eine Blase nie mit
// einem Brocken verwechselt werden kann.
//
// **Wirkung ist Reibung oder Belohnung, nie Beschleunigung** (`dragMul >= 1`):
// ein schnelleres Schiff liesse die Bewegungspruefung des Servers ehrliche
// Meldungen still verwerfen.

import { anomalyKind, balancing, zoneAtDist } from '../data/balancing';
import { jsRound } from '../core/math';
import { hash3, rngFrom } from '../core/rng';
import { eraSeed } from './field';

/** Eine Blase, wie sie beide Seiten unabhaengig ausrechnen. */
export interface Anomaly {
  id: string;
  kindId: string;
  x: number;
  y: number;
  r: number;
  zoneIdx: number;
}

/**
 * Die Wirkung aller Blasen an einem Ort — neutral ausserhalb.
 *
 * Bei Ueberlappung gilt je Wert das **Maximum** (bzw. Oder): eine zweite Blase
 * macht nichts schwaecher. Der Server rechnet wortgleich.
 */
export interface AnomalyEffect {
  dragMul: number;
  gemLuckMul: number;
  fireFree: boolean;
  /** Energie je Sekunde, die die Blase auch draussen nachlaedt. */
  regen: number;
}

/** Die Blasen eines Chunks. **Die Reihenfolge der Ziehungen ist Vertrag.** */
export function anomaliesInChunk(
  cx: number,
  cy: number,
  seed: number,
  era: number = balancing().eraFirst,
): Anomaly[] {
  const world = balancing().world;
  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(cx, cy, welt), world.anomalySalt, welt));

  if (rng() >= world.anomalyChunkChance) return [];                     // 1

  const x = jsRound((cx + rng()) * world.chunk);                        // 2
  const y = jsRound((cy + rng()) * world.chunk);                        // 3
  const pick = rng();                                                   // 4
  const size = rng();                                                   // 5

  // Um die Kapsel herum bleibt das Feld frei — auch von Blasen.
  const dist = Math.hypot(x, y);
  if (dist < world.clearRadius * 2) return [];

  const zoneIdx = zoneAtDist(dist, era);
  const arten = balancing().anomalies.filter((kind) => kind.minZone <= zoneIdx);
  if (!arten.length) return [];

  const kind = arten[Math.min(arten.length - 1, Math.floor(pick * arten.length))];
  const r = jsRound(kind.r[0] + size * (kind.r[1] - kind.r[0]));
  return [{ id: `a:${cx}:${cy}:0`, kindId: kind.id, x, y, r, zoneIdx }];
}

/**
 * Die Blasen um das Schiff herum — nachgeladen je Chunk, wie `HostileField`.
 *
 * `effectAt` schreibt in **ein** wiederverwendetes Objekt statt je Frame ein
 * neues zu bauen: die Abfrage laeuft 60-mal pro Sekunde (Gate FG-14, dieselbe
 * Idee wie die Zielpuffer).
 */
export class AnomalyField {
  private readonly live = new Map<string, Anomaly>();
  private readonly loaded = new Set<string>();
  private readonly effect: AnomalyEffect = { dragMul: 1, gemLuckMul: 1, fireFree: false, regen: 0 };

  constructor(
    private seed: number,
    /** Die Ära des Feldes — wie bei `World`, nicht je Abfrage. */
    private era: number = balancing().eraFirst,
  ) {}

  reset(seed: number, era: number = balancing().eraFirst): void {
    this.seed = seed;
    this.era = era;
    this.live.clear();
    this.loaded.clear();
  }

  all(): IterableIterator<Anomaly> {
    return this.live.values();
  }

  /** Chunks um das Schiff herum nachladen. */
  update(x: number, y: number): void {
    const world = balancing().world;
    const cx = Math.floor(x / world.chunk);
    const cy = Math.floor(y / world.chunk);
    const radius = world.loadRadius;

    for (let ix = cx - radius; ix <= cx + radius; ix++) {
      for (let iy = cy - radius; iy <= cy + radius; iy++) {
        const key = `${ix}:${iy}`;
        if (this.loaded.has(key)) continue;
        this.loaded.add(key);
        for (const anomaly of anomaliesInChunk(ix, iy, this.seed, this.era)) {
          if (!this.live.has(anomaly.id)) this.live.set(anomaly.id, anomaly);
        }
      }
    }
  }

  /** Die Blase an einem Ort — `null` ausserhalb. Erste Treffer gewinnt fuers HUD. */
  at(x: number, y: number): Anomaly | null {
    for (const anomaly of this.live.values()) {
      const dx = anomaly.x - x;
      const dy = anomaly.y - y;
      if (dx * dx + dy * dy <= anomaly.r * anomaly.r) return anomaly;
    }
    return null;
  }

  /** Die Wirkung an einem Ort. Gibt das wiederverwendete Objekt zurueck. */
  effectAt(x: number, y: number): AnomalyEffect {
    const out = this.effect;
    out.dragMul = 1;
    out.gemLuckMul = 1;
    out.fireFree = false;
    out.regen = 0;
    for (const anomaly of this.live.values()) {
      const dx = anomaly.x - x;
      const dy = anomaly.y - y;
      if (dx * dx + dy * dy > anomaly.r * anomaly.r) continue;
      const kind = anomalyKind(anomaly.kindId);
      if (!kind) continue;
      out.dragMul = Math.max(out.dragMul, kind.dragMul);
      out.gemLuckMul = Math.max(out.gemLuckMul, kind.gemLuckMul);
      out.fireFree = out.fireFree || kind.fireFree;
      out.regen = Math.max(out.regen, kind.regen);
    }
    return out;
  }
}
